/**
 * Frontend client for the Collections / lookbooks API
 * (server/modules/collections). Workspace-scoped calls go through the shared
 * client (auth token + active-workspace header); the public lookbook resolve is
 * a plain unauthenticated fetch.
 */
import { apiGet, apiPost, apiPatch, apiPut, apiDelete } from "../shared/apiClient";

const API_BASE = "/api/v1";

export type SharedAccess = "view" | "download" | "comment" | "approve";

export interface CollectionItem {
  fileId: string;
  position: number;
}

export interface Collection {
  _id: string;
  name: string;
  intro?: string;
  items: CollectionItem[];
  coverFileIds: string[];
  shareToken: string;
  shareEnabled: boolean;
  sharedClientName?: string;
  sharedAccess: SharedAccess;
  viewCount: number;
  downloadCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCollectionInput {
  name: string;
  intro?: string;
  fileIds?: string[];
}

export interface UpdateCollectionInput {
  name?: string;
  intro?: string;
  shareEnabled?: boolean;
  sharedClientName?: string;
  sharedAccess?: SharedAccess;
  coverFileIds?: string[];
}

/** Public lookbook shape returned by the token resolve. */
export interface LookbookItem {
  id: string;
  name: string;
  posterUrl: string | null;
}
export interface ResolvedLookbook {
  name: string;
  intro?: string;
  clientName?: string;
  access: SharedAccess;
  items: LookbookItem[];
}

export const listCollections = () => apiGet<Collection[]>("/collections");

export const getCollection = (id: string) => apiGet<Collection>(`/collections/${id}`);

export const createCollection = (input: CreateCollectionInput) =>
  apiPost<Collection>("/collections", input);

export const updateCollection = (id: string, patch: UpdateCollectionInput) =>
  apiPatch<Collection>(`/collections/${id}`, patch);

export const deleteCollection = (id: string) =>
  apiDelete<{ id: string }>(`/collections/${id}`);

export const addItems = (id: string, fileIds: string[]) =>
  apiPost<Collection>(`/collections/${id}/items`, { fileIds });

export const removeItem = (id: string, fileId: string) =>
  apiDelete<Collection>(`/collections/${id}/items/${fileId}`);

export const reorderItems = (id: string, orderedFileIds: string[]) =>
  apiPut<Collection>(`/collections/${id}/items/order`, { orderedFileIds });

/** Public — no auth; resolves a shared lookbook by its token. */
export const resolveLookbook = (token: string) =>
  fetch(`${API_BASE}/collections/${token}/resolve`)
    .then((r) => r.json())
    .then((j) => (j?.success ? (j.data as ResolvedLookbook) : null))
    .catch(() => null);
